/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useMemo, useRef, useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

import set from "lodash/set";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { UPDATE_DIGITAL_RECEPTION_THEME } from "../../../../config/graphql/mutation";
import { QUERY_DIGITAL_RECEPTION_THEME } from "../../../../config/graphql/query";

import { upload } from "../../../../lib/hooks/useSocket";
import { useDomainCheck } from "../../../../lib/hooks/useDomainCheck";

import ColorPicker from "../../../../components/ColorPicker";
import ImageCropPicker from "../../../../components/ImageCropPicker";
import { UncontrolledUploadInput } from "../../../../components/FileUpload";

import { readFileAsDataURL } from "../../../../lib/common/readFileAsDataURL";

import {
  CUSTOMIZABLE_THEME_COLORS_RECEPTIONS,
  CUSTOMIZABLE_THEME_LOGO_RECEPTIONS,
  CUSTOMIZABLE_THEME_VIDEO_RECEPTIONS,
} from "../../../../config/const/common";

// The upload travels as base64 in a single WebSocket message, so the whole file sits in memory
// on both ends. Past this it stops being a kiosk asset and starts being a problem.
const MAX_HERO_VIDEO_BYTES = 15 * 1024 * 1024;

interface FieldValues extends Pick<IDigitalReceptionTheme, "colors"> {
  images?: {
    hero?: { file: string; filename: string };
    heroVideo?: { id: string; filename: string; absolutePath: string } | null;
    logo?: { file: string; filename: string };
  };
}

const ThemeRoute = () => {
  const methods = useForm<FieldValues>({ shouldUnregister: true });

  const { screenId } = useParams<{ screenId: string }>();

  const { isFrontAssistDomain } = useDomainCheck();

  const previewRef = useRef<HTMLIFrameElement>(null);

  const { t } = useTranslation(["screens", "common"]);

  const postMessage = useCallback((message: string, targetOrigin = "*") => {
    return previewRef?.current?.contentWindow?.postMessage(
      message,
      targetOrigin,
    );
  }, []);

  const onChangePreview = useCallback(
    (name: string, value: any) => {
      const data = {};

      set(data, ["digitalReception", "theme", ...name.split(".")], value);

      const message = JSON.stringify({
        action: "update-digitalReception",
        data,
      });

      return postMessage(message);
    },
    [postMessage],
  );

  const { data: screenData } = useQuery<{
    digitalReception: IDigitalReception;
  }>(QUERY_DIGITAL_RECEPTION_THEME, {
    skip: !screenId,
    variables: {
      id: screenId,
    },
    onCompleted: ({ digitalReception: { theme } }) => {
      methods.reset({
        colors: theme?.colors,
      });
    },
  });

  const template = screenData?.digitalReception?.template;

  const isColorCustomizable = useMemo(
    () => CUSTOMIZABLE_THEME_COLORS_RECEPTIONS.includes(template || ""),
    [template],
  );

  const isLogoCustomizable = useMemo(
    () => CUSTOMIZABLE_THEME_LOGO_RECEPTIONS.includes(template || ""),
    [template],
  );

  const isVideoCustomizable = useMemo(
    () => CUSTOMIZABLE_THEME_VIDEO_RECEPTIONS.includes(template || ""),
    [template],
  );

  const currentVideoName =
    screenData?.digitalReception?.theme?.images?.heroVideo?.filename;

  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  const [onUpdate] = useMutation(UPDATE_DIGITAL_RECEPTION_THEME, {
    refetchQueries: [
      {
        query: QUERY_DIGITAL_RECEPTION_THEME,
        variables: { id: screenId },
      },
    ],
  });

  const onUpload = useCallback(async (images: FieldValues["images"]) => {
    let input: { hero?: string; heroVideo?: string | null; logo?: string } = {};

    if (images?.hero) {
      await upload([images.hero]).then((r) => {
        input = {
          ...input,
          hero: r[0].id,
        };
      });
    }

    // Sent as null when the video was removed, which is what clears the stored one: the server
    // merges the theme, and a merge passes null through while it skips undefined.
    if (images?.heroVideo !== undefined) {
      input = {
        ...input,
        heroVideo: images.heroVideo?.id ?? null,
      };
    }

    if (images?.logo) {
      await upload([images.logo]).then((r) => {
        input = {
          ...input,
          logo: r[0].id,
        };
      });
    }

    return input;
  }, []);

  const onSubmit = useCallback(
    async ({ images, ...rest }: FieldValues) => {
      const uploadedImages = await onUpload(images).catch(() => null);

      if (!uploadedImages) {
        toast.error<string>(t("screens:screen.screenRoute.toast.uploadFailed"));
        return;
      }

      const theme = {
        ...rest,
        images: uploadedImages,
      };

      onUpdate({
        variables: {
          input: {
            id: screenId,
            theme,
          },
        },
      }).then(() => {
        toast.success<string>(t("screens:screen.screenRoute.toast.updated"));

        const previewEl = document.querySelector<HTMLIFrameElement>("#preview");

        if (previewEl) {
          previewEl.src += "";
        }
      });
    },
    [onUpdate, onUpload, t, screenId],
  );

  return (
    <FormProvider {...methods}>
      <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="col-lg-4">
          {/* <div className="form-group mb-4">
            <label htmlFor="welcomeMessage">
              {t("screens:screen.theme.welcomeMessage")}
            </label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  {language.toUpperCase()}
                </span>
              </div>
              <input
                {...methods.register("welcomeMessage", {
                  onChange: ({ target: { value } }) => {
                    onChangePreview(`welcomeMessage.${language}`, value);
                  },
                })}
                className="form-control"
              />
            </div>
          </div> */}

          {isColorCustomizable && (
            <>
              <Controller
                name="colors.primary"
                render={({ field: { value, name, onChange } }) => (
                  <div className="form-group">
                    <ColorPicker
                      label={t("screens:screen.theme.color.primary")}
                      value={
                        // eslint-disable-next-line no-nested-ternary
                        typeof value === "string"
                          ? value
                          : isFrontAssistDomain
                          ? "#33435d"
                          : "#2D9CCA"
                      }
                      onChange={(value) => {
                        onChange(value);

                        onChangePreview(name, value);
                      }}
                    />
                  </div>
                )}
              />
              <Controller
                name="colors.background"
                render={({ field: { value, name, onChange } }) => (
                  <div className="form-group">
                    <ColorPicker
                      label={t("screens:screen.theme.color.background")}
                      value={typeof value === "string" ? value : "#ffffff"}
                      onChange={(value) => {
                        onChange(value);

                        onChangePreview(name, value);
                      }}
                    />
                  </div>
                )}
              />
              <Controller
                name="colors.textPrimary"
                render={({ field: { value, name, onChange } }) => (
                  <div className="form-group">
                    <ColorPicker
                      label={t("screens:screen.theme.color.primaryText")}
                      value={typeof value === "string" ? value : "#212529"}
                      onChange={(value) => {
                        onChange(value);

                        onChangePreview(name, value);
                      }}
                    />
                  </div>
                )}
              />
            </>
          )}
          <Controller
            name="images.hero"
            render={({ field: { name, value, onChange } }) => (
              <div className="form-group">
                <label htmlFor={name}>
                  {t("screens:screen.theme.image.hero")}
                </label>
                <ImageCropPicker
                  name={name}
                  aspect={1080 / 545}
                  value={value}
                  onChange={(fileBase64) => {
                    onChange(fileBase64);

                    onChangePreview(`${name}.absolutePath`, fileBase64.file);
                  }}
                />
                <small className="form-text text-muted">
                  {t("screens:screen.theme.image.description")}
                </small>
              </div>
            )}
          />
          {isVideoCustomizable && (
            <Controller
              name="images.heroVideo"
              render={({ field: { name, value, onChange } }) => (
                <div className="form-group">
                  <label htmlFor={name}>
                    {t("screens:screen.theme.video.hero")}
                  </label>
                  <UncontrolledUploadInput
                    name={name}
                    multiple={false}
                    dropZoneOptions={{
                      accept: {
                        "video/mp4": [".mp4"],
                        "video/webm": [".webm"],
                      },
                      maxSize: MAX_HERO_VIDEO_BYTES,
                      // Without this a file that is too big or of the wrong kind is simply
                      // ignored, and the admin is left waiting for something that never happens.
                      onDropRejected: () =>
                        toast.error<string>(
                          t("screens:screen.theme.video.rejected"),
                        ),
                    }}
                    onChange={async (file) => {
                      setIsUploadingVideo(true);

                      try {
                        const { data } = await readFileAsDataURL(file);

                        const [uploaded] = await upload([
                          { file: data, filename: file.name },
                        ]);

                        const heroVideo = {
                          id: uploaded.id,
                          filename: uploaded.filename,
                          absolutePath: uploaded.absolutePath,
                        };

                        onChange(heroVideo);

                        // Sent whole, id included: the reception builds the media URL with the id
                        // on it, so without the new one the preview would ask for the URL it
                        // already has and be served the old video.
                        onChangePreview(name, heroVideo);
                      } catch {
                        toast.error<string>(
                          t("screens:screen.screenRoute.toast.uploadFailed"),
                        );
                      } finally {
                        setIsUploadingVideo(false);
                      }
                    }}
                  />
                  <small className="form-text text-muted">
                    {t("screens:screen.theme.video.description")}
                  </small>
                  {isUploadingVideo && (
                    <small className="form-text text-muted">
                      {t("screens:screen.theme.video.uploading")}
                    </small>
                  )}
                  {!isUploadingVideo &&
                    value !== null &&
                    !!(value?.filename || currentVideoName) && (
                      <small className="form-text text-muted">
                        {t("screens:screen.theme.video.current")}:{" "}
                        {value?.filename || currentVideoName}{" "}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 align-baseline"
                          onClick={() => {
                            onChange(null);

                            onChangePreview(name, null);
                          }}
                        >
                          {t("screens:screen.theme.video.remove")}
                        </button>
                      </small>
                    )}
                </div>
              )}
            />
          )}
          {isLogoCustomizable && (
            <Controller
              name="images.logo"
              render={({ field: { name, value, onChange } }) => (
                <div className="form-group">
                  <label htmlFor={name}>
                    {t("screens:screen.theme.image.logo")}
                  </label>
                  <ImageCropPicker
                    name={name}
                    allowsEditing={false}
                    value={value}
                    onChange={(fileBase64) => {
                      onChange(fileBase64);

                      onChangePreview(`${name}.absolutePath`, fileBase64.file);
                    }}
                  />
                  <small className="form-text text-muted">
                    {t("screens:screen.theme.image.description")}
                  </small>
                </div>
              )}
            />
          )}
        </div>
        <div className="col-lg-8 row">
          <div className="col-12 mb-2">
            <div
              className=" position-absolute p-0"
              style={{ zIndex: 100, width: 600 / (1920 / 1080), height: 600 }}
            />
            <iframe
              id="preview"
              ref={previewRef}
              title={`screen-${screenId}`}
              className="border border-dark"
              src={`${screenData?.digitalReception?.previewUrl?.replace(
                ":8080",
                ":9000",
              )}`}
              style={{
                width: 600 / (1920 / 1080),
                height: 600,
              }}
              frameBorder={0}
              marginHeight={0}
              marginWidth={0}
            />
          </div>
        </div>
        <div className="col-12">
          <input type="submit" className="btn btn-primary" />
        </div>
      </form>
    </FormProvider>
  );
};

export default ThemeRoute;
